import { Injectable, inject, signal } from '@angular/core';
import { StorageManagerService } from './storage-manager.service';
import { BehaviorSubject, retry, timeout } from 'rxjs';
import { LocalStorageUser } from '@utils/interfaces/localStorage';
import { RefreshTokenService } from './refresh-token.service';
import { Router } from '@angular/router';
import { transformToTitleCase } from 'logical-growth-components';
import {
  NotificationService,
  NotificationSeverity,
} from './notification.service';

enum SessionCheckedStatus {
  checking = 'checking',
  checked = 'checked',
  standBy = 'stand_by',
}

@Injectable({
  providedIn: 'root',
})

export class SessionService extends StorageManagerService {

  public checkStatuses = SessionCheckedStatus;
  private _checkingSession$ = new BehaviorSubject<SessionCheckedStatus>(
    this.checkStatuses.standBy
  );
  public checkingSession$ = this._checkingSession$.asObservable();

  private _authToken= signal<string | null>(null);
  private _authToken$ = new BehaviorSubject(this._authToken());
  public authToken$ = this._authToken$.asObservable();

  private _refreshToken= signal<string | null>(null);
  private _refreshToken$ = new BehaviorSubject(this._refreshToken());
  public refreshToken$ = this._refreshToken$.asObservable();

  private _user= signal<LocalStorageUser | null>(null);
  private _user$ = new BehaviorSubject(this._user());
  public user$ = this._user$.asObservable();

  public readonly REFRESH_TOKEN_STORAGE_NAME = 'REFRESH_TOKEN';
  public readonly TOKEN_STORAGE_NAME = 'ACCESS_TOKEN';
  public readonly USER_STORAGE_NAME = 'USER';
  public readonly KEEP_SESSION_NAME = 'KEEP_COBRANZA_SESSION';

  public refreshTokenSession = inject(RefreshTokenService);
  public router = inject(Router);
  private _notifications = inject(NotificationService);

  public storage_events_handler = (event: StorageEvent) => {
    if (event.key === this.REFRESH_TOKEN_STORAGE_NAME) {
      if (event.newValue === event.oldValue) return;
      if (event.newValue){
        this.refreshToken = event.newValue;
      }
    }

    if (event.key === this.USER_STORAGE_NAME) {
      if (event.newValue === event.oldValue) return;
      if (event.newValue){
        this.user = JSON.parse(event.newValue);
      }
    }

    if (event.key === this.TOKEN_STORAGE_NAME) {
      if (event.newValue === event.oldValue) return;
      if (event.newValue){
        this.authToken = event.newValue;
      }
    }
  };

  get authToken(): string | null {
    return this._authToken();
  }

  set authToken(token: string | null) {
    this._authToken.set(token);
    this._authToken$.next(this._authToken());
    this.addValueToken('local', this.TOKEN_STORAGE_NAME, this._authToken() || 'null');
  }

  get refreshToken(): string | null {
    return this._refreshToken();
  }

  set refreshToken(token: string | null) {
    this._refreshToken.set(token);
    this._refreshToken$.next(this._refreshToken());
    this.addValueToken(
      'local',
      this.REFRESH_TOKEN_STORAGE_NAME,
      this._refreshToken() || 'null'
    );
  }

  get user(): LocalStorageUser | null {
    const value = structuredClone(this._user());
    return value;
  }

  set user(user: LocalStorageUser | null) {
    this._user.set(user);
    this._user$.next(this._user());
    this.addValue('local', this.USER_STORAGE_NAME, this._user());
  }

  public get isAdmin(): boolean {
    return this.user?.type === 'admin';
  }

  public checkSession(): void {
    this.authToken = this.getValueToken('local', this.TOKEN_STORAGE_NAME);
    this.refreshToken = this.getValueToken(
      'local',
      this.REFRESH_TOKEN_STORAGE_NAME
    );
    this.user = this.getValue('local', this.USER_STORAGE_NAME);
    const waitTime = 3000;
    const requestRetry = 1;

    const keep_logged_in: boolean =
      this.getValue('local', this.KEEP_SESSION_NAME) ?? false;

    const isPrivatePath = window.location.pathname.includes('priv');
    const isPublicPath = !window.location.pathname.includes('priv');

    const redirectionPath =
      window.location.pathname === '/'
        ? '/auth/user'
        : window.location.pathname;
    const queryParams = window.location.search ?? '';

    if (isPrivatePath && !this.refreshToken) {
      this.router.navigateByUrl('/auth/company');
      return;
    }

    if (isPublicPath && !this.refreshToken) {
      this.router.navigateByUrl(redirectionPath + queryParams);
      return;
    }

    this._checkingSession$.next(this.checkStatuses.checking);
    if (this.refreshToken !== 'null' && this.refreshToken !== null) {
      this.refreshTokenSession
        .updatePartial(this.refreshToken, { keep_logged_in })
        .pipe(timeout(waitTime), retry(requestRetry))
        .subscribe({
          next: async (value: unknown) => {
            const { refresh, token, ...others } = value as LocalStorageUser;
            await this.startSession({
              data: {
                ...others,
                refresh,
                token,
              },
              keepSession: keep_logged_in,
              defaultNavigation: true,
            });
            this._checkingSession$.next(this.checkStatuses.checked);
          },
          error: () => {
            this._checkingSession$.next(this.checkStatuses.checked);
            this.clear();
            this.router.navigateByUrl('/auth/user');
          },
        });
    }
  }

  public storeCredentialsToLocalStorage({
    data,
    keepSession = false,
  }: {
    data: LocalStorageUser;
    keepSession: boolean;
  }): Promise<void> {
    return new Promise((resolve) => {
      this.authToken = data.token;
      this.refreshToken = data.refresh;
      this.user = data;
      this.addValue('local', this.KEEP_SESSION_NAME, keepSession);
      resolve();
    });
  }

  /**
   * [validateAccountRedirection]
   * @param defaultNavigation: Specifies if use the current pathname, in case no other condition is met
   */
  public validateAccountRedirection({
    defaultNavigation = false,
  }: {
    defaultNavigation: boolean;
  }): Promise<boolean> {

    if (!this?.user) {
      return this.router.navigateByUrl('/auth/user');
    }

    if (this.user.validate_otp === false) {
      return this.router.navigateByUrl('/auth/validar');
    }

    if (this.user.need_password_renew === true) {
      return this.router.navigate([`/auth/actualizar-contrasena`]);
    }

    if (defaultNavigation) {
      const currentPath = window.location.pathname;
      const queryParams = window.location.search;
      return this.router.navigateByUrl(currentPath + queryParams);
    } else {
      this._notifications.create({
        severity: NotificationSeverity.success,
        summary: 'Inicio de sesión exitoso',
        detail: `Bienvenido, ${transformToTitleCase(this.user.name)}`,
        sticky: false,
      });
      return this.router.navigateByUrl('/priv');
    }
  }

  public async startSession({
    data,
    keepSession = false,
    defaultNavigation = false,
  }: {
    data: LocalStorageUser;
    keepSession?: boolean;
    defaultNavigation?: boolean;
  }): Promise<void> {
    await this.storeCredentialsToLocalStorage({
      data,
      keepSession,
    });
    await this.validateAccountRedirection({
      defaultNavigation: defaultNavigation,
    });
  }

  public clear(): void {
    this._authToken.set(null);
    this._refreshToken.set(null);
    this._user.set(null);

    this._authToken$.next(null);
    this._refreshToken$.next(null);
    this._user$.next(null);

    this.clearStorage('local');
  }
}
